// Pricing-Simulation für Aivy-Admins. Schaltet die Sperre unabhängig vom Flag
// und simuliert ein Paket, beides nur in diesem Browser. Jede Auswahl wirkt
// sofort, weil PaymentContext und dieser Dialog denselben Store lesen
// (features/pricing/admin-override.js).
//
// Admin-Texte bleiben hartcodiert Deutsch wie die übrigen Admin-Werkzeuge.
import {
  Description,
  DialogTitle,
  Label,
  Radio,
  RadioGroup
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'

import { usePartnerContext, useUserContext } from '../../context'
import {
  ADMIN_ENFORCEMENT_OPTIONS,
  ADMIN_PLAN_OPTIONS,
  usePricingAdminOverride
} from '../../features/pricing/admin-override'
import { usePricingEnforcementFlag } from '../../features/pricing/enforcement-flag'
import {
  ADMIN_ENFORCEMENT,
  MODES,
  REAL_PLAN
} from '../../features/pricing/entitlements'
import { useEntitlements } from '../../features/pricing/hooks'
import { planOf } from '../../features/pricing/payment-selection'
import { PLANS } from '../../features/pricing/plans'
import { classNames } from '../../helpers'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'

export const ENFORCEMENT_LABELS = Object.freeze({
  flag: 'Wie Flag',
  on: 'An',
  off: 'Aus'
})

export const planLabel = (planId) =>
  planId === REAL_PLAN ? 'Echt' : PLANS[planId]?.name || planId

const MODE_DESCRIPTIONS = Object.freeze({
  [MODES.LEGACY]: 'ohne Plan-Payment, alles wie bisher',
  [MODES.PREVIEW]: 'Paket und Nutzung sichtbar, keine Sperren',
  [MODES.ENFORCED]: 'Sperren und Limits aktiv'
})

/**
 * Echtes Paket des Kunden, auch während einer Simulation. resolveEntitlements
 * liefert es als entitlements.realPlan. Ohne das Feld gilt ohne Simulation
 * entitlements.plan, mit Simulation wird über planById aufgelöst.
 *
 * @returns {{ id: string, name: string|null }|null}
 */
export const realPlanOf = (entitlements, planById = {}) => {
  const payment = entitlements?.realPlanPayment
  if (!payment) return null
  if (entitlements.realPlan) {
    return { id: entitlements.realPlan.id, name: entitlements.realPlan.name }
  }
  if (!entitlements.isSimulated && entitlements.plan) {
    return { id: entitlements.plan.id, name: entitlements.plan.name }
  }
  const id = planOf(payment, planById || {})
  return id ? { id, name: PLANS[id]?.name || null } : null
}

const flagStateLabel = ({ on, loaded }) => {
  if (!loaded) return 'lädt'
  return on ? 'an' : 'aus'
}

// Segment-Gruppe im Stil der Rollen-Pills der Mitgliederliste
const SegmentGroup = ({ label, value, onChange, options }) => (
  <RadioGroup value={value} onChange={onChange}>
    <Label className='block text-sm font-medium text-gray-900'>{label}</Label>
    <div className='mt-2 inline-flex max-w-full flex-wrap gap-0.5 rounded-3xl bg-gray-100 p-1'>
      {options.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          className={({ checked, focus }) =>
            classNames(
              'inline-flex cursor-pointer items-center gap-x-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-semibold transition-all focus:outline-none',
              checked
                ? 'bg-white text-primaryBlue shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
              focus && 'ring-1 ring-inset ring-blue-600'
            )
          }
        >
          {option.label}
          {option.hint && (
            <span className='font-medium opacity-60'>{option.hint}</span>
          )}
        </Radio>
      ))}
    </div>
  </RadioGroup>
)

SegmentGroup.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      hint: PropTypes.string
    })
  ).isRequired
}

const InfoRow = ({ label, children }) => (
  <div className='flex items-baseline justify-between gap-4 py-2'>
    <dt className='shrink-0 text-gray-500'>{label}</dt>
    <dd className='min-w-0 text-right text-gray-900'>{children}</dd>
  </div>
)

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

const PaymentId = ({ id }) => (
  <span
    className='block select-all truncate font-[ui-monospace,SFMono-Regular,Menlo,monospace] text-xs text-gray-700'
    title={id}
  >
    {id}
  </span>
)

PaymentId.propTypes = { id: PropTypes.string.isRequired }

const AdminPricingDialog = ({ open, onClose }) => {
  const { isAdmin } = useUserContext()
  const { partner } = usePartnerContext()
  const entitlements = useEntitlements()
  const flag = usePricingEnforcementFlag(partner?.id)
  const { available, active, override, setEnforcement, setPlan, reset } =
    usePricingAdminOverride({ isAdmin, partnerId: partner?.id })

  if (!available) return null

  const realPlan = realPlanOf(entitlements)
  const realPlanPayment = entitlements.realPlanPayment
  const legacyPayment = entitlements.legacyPayment

  const enforcementOptions = ADMIN_ENFORCEMENT_OPTIONS.map((value) => ({
    value,
    label: ENFORCEMENT_LABELS[value],
    hint: value === ADMIN_ENFORCEMENT.FLAG ? flagStateLabel(flag) : null
  }))

  const planOptions = ADMIN_PLAN_OPTIONS.map((value) => ({
    value,
    label: planLabel(value),
    hint: value === REAL_PLAN && realPlan ? realPlan.name || realPlan.id : null
  }))

  return (
    <Modal
      open={open}
      setOpen={onClose}
      size='md'
      padding='p-0'
      borderRadius='rounded-xl'
      hideCloseButton
    >
      <div className='flex items-start justify-between gap-4 px-5 pt-5'>
        <div>
          <DialogTitle className='text-base font-semibold text-gray-900'>
            Pricing-Simulation
          </DialogTitle>
          <Description className='mt-1 text-sm text-gray-500'>
            Nur für Aivy-Admins und nur in diesem Browser. Kunden sehen davon
            nichts.
          </Description>
        </div>
        <button
          type='button'
          onClick={onClose}
          aria-label='Schließen'
          className='-mr-1 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600'
        >
          <XMarkIcon className='size-5' aria-hidden='true' />
        </button>
      </div>

      <div className='space-y-5 px-5 pt-5'>
        <SegmentGroup
          label='Pricing-Sperre'
          value={override.enforcement}
          onChange={setEnforcement}
          options={enforcementOptions}
        />
        <SegmentGroup
          label='Paket simulieren'
          value={override.plan}
          onChange={setPlan}
          options={planOptions}
        />
      </div>

      <dl className='mx-5 mt-5 divide-y divide-gray-100 rounded-lg bg-gray-50 px-4 text-sm'>
        <InfoRow label='Echtes Paket'>
          {realPlan
            ? `${realPlan.name || realPlan.id} (${realPlan.id})`
            : realPlanPayment
              ? 'Plan-Payment, Paket nicht auflösbar'
              : 'kein Paket hinterlegt'}
        </InfoRow>
        {realPlanPayment?.id && (
          <InfoRow label='Plan-Payment'>
            <PaymentId id={realPlanPayment.id} />
          </InfoRow>
        )}
        {legacyPayment?.id && (
          <InfoRow label='Altpayment'>
            <PaymentId id={legacyPayment.id} />
          </InfoRow>
        )}
        <InfoRow label='Modus'>
          <span className='font-[ui-monospace,SFMono-Regular,Menlo,monospace] text-xs'>
            {entitlements.mode}
          </span>
          <span className='block text-xs text-gray-500'>
            {MODE_DESCRIPTIONS[entitlements.mode]}
            {!entitlements.settled && ', Stand lädt noch'}
          </span>
        </InfoRow>
      </dl>

      <div className='mt-5 flex justify-end gap-2 border-t border-gray-100 px-5 py-4'>
        <Button.SecondaryBase
          text='Zurücksetzen'
          onClick={reset}
          disabled={!active}
          minWidth='min-w-0'
        />
        <Button.PrimaryBase text='Fertig' onClick={onClose} />
      </div>
    </Modal>
  )
}

AdminPricingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}

export default AdminPricingDialog
